import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Store } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/useSubscription";
import { plans } from "@/config/stripePlans";
import { useOutlet } from "@/contexts/OutletContext";

export default function OutletsManagement() {
  const { outlets, refreshOutlets } = useOutlet();
  const { subscriptionTier } = useSubscription();
  const [pendingOutletToggle, setPendingOutletToggle] = useState<{ id: string; name: string; nextActive: boolean } | null>(null);
  const [outletToggleLoading, setOutletToggleLoading] = useState(false);
  const [lastOutletWarningOpen, setLastOutletWarningOpen] = useState(false);

  const currentPlan = (subscriptionTier || "free").toLowerCase();
  const plan = plans[currentPlan];
  const outletLimit: number | null = plan?.limits?.outlets ?? null;

  const outletItems = outlets.map((o) => ({
    id: o.outlet_id,
    name: o.outlet_name,
    location: o.address || "No address provided",
    isActive: o.is_active ?? true,
  }));

  const handleConfirmOutletToggle = async () => {
    if (!pendingOutletToggle) return;
    setOutletToggleLoading(true);
    try {
      const { error } = await supabase
        .from("outlets")
        .update({ is_active: pendingOutletToggle.nextActive })
        .eq("outlet_id", pendingOutletToggle.id);
      if (error) throw error;
      await refreshOutlets();
      toast({
        title: "Outlet updated",
        description: `Outlet is now ${pendingOutletToggle.nextActive ? "active" : "inactive"}.`,
      });
      setPendingOutletToggle(null);
    } catch (error: any) {
      console.error("Error updating outlet:", error);
      toast({
        title: "Error updating outlet",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setOutletToggleLoading(false);
    }
  };

  const icon = <Store className="h-4 w-4 text-primary" />;

  const filledSlots = outletItems.map((item, i) => (
    <div
      key={item.id || i}
      className={`border rounded-xl px-4 py-3 flex items-center justify-between gap-3 ${item.isActive === false ? "opacity-60 bg-muted/30" : ""}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.isActive === false ? "bg-muted" : "bg-primary/10"}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium truncate flex items-center gap-2">
            {item.name}
            {item.isActive === false && (
              <Badge variant="outline" className="text-[10px] h-4 px-1 pb-0 shadow-none">Inactive</Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground truncate">{item.location || ""}</div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[10px] text-muted-foreground font-medium hidden sm:inline-block">
          {item.isActive ? "Active" : "Inactive"}
        </span>
        <Switch
          checked={item.isActive}
          onCheckedChange={(checked) => {
            if (!checked) {
              const activeCount = outlets.filter((o) => o.is_active !== false).length;
              if (activeCount <= 1) {
                setLastOutletWarningOpen(true);
                return;
              }
            }
            setPendingOutletToggle({ id: item.id, name: item.name, nextActive: checked });
          }}
        />
      </div>
    </div>
  ));

  const emptyCount = outletLimit !== null ? Math.max(0, outletLimit - outletItems.length) : 0;
  const emptySlots = Array.from({ length: emptyCount }, (_, i) => (
    <div key={`empty-${i}`} className="border border-dashed rounded-xl px-4 py-3 flex items-center gap-3 opacity-40">
      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">{icon}</div>
      <div className="text-sm text-muted-foreground">Available slot</div>
    </div>
  ));
  const upgradeCard = outletLimit !== null ? (
    <div className="border border-dashed rounded-xl px-4 py-3 flex items-center gap-3 text-muted-foreground">
      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
        <Plus className="h-4 w-4" />
      </div>
      <div className="text-xs">Upgrade plan to add more outlets</div>
    </div>
  ) : null;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-heading font-semibold text-foreground">Outlets</h2>
        <p className="text-sm text-muted-foreground">
          Manage your business locations and control which outlets are visible to customers
        </p>
      </div>

      <div className="p-6 border rounded-xl bg-card shadow-sm">
        <div className="mb-6">
          <p className="text-base font-heading font-semibold text-foreground">Outlets</p>
          <p className="text-sm text-muted-foreground mt-1">
            {outletItems.filter((o) => o.isActive).length} of {outletLimit === null ? "unlimited" : outletLimit} outlets used
          </p>
        </div>

        <div className="space-y-2">
          {filledSlots}
          {emptySlots}
          {upgradeCard}
        </div>
      </div>

      {/* Outlet toggle confirmation */}
      <AlertDialog
        open={!!pendingOutletToggle}
        onOpenChange={(open) => {
          if (!open && !outletToggleLoading) setPendingOutletToggle(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingOutletToggle?.nextActive
                ? `Activate ${pendingOutletToggle?.name}?`
                : `Deactivate ${pendingOutletToggle?.name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingOutletToggle?.nextActive
                ? "This outlet will become visible to customers and available for bookings."
                : "This outlet will be hidden from customers and unavailable for new bookings until reactivated."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={outletToggleLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmOutletToggle();
              }}
              className={
                pendingOutletToggle?.nextActive
                  ? ""
                  : "bg-destructive text-destructive-foreground hover:bg-destructive/90"
              }
              disabled={outletToggleLoading}
            >
              {outletToggleLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {pendingOutletToggle?.nextActive ? "Activate" : "Deactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Last active outlet warning */}
      <AlertDialog open={lastOutletWarningOpen} onOpenChange={setLastOutletWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot deactivate last outlet</AlertDialogTitle>
            <AlertDialogDescription>
              At least one outlet must remain active. Activate another outlet first before deactivating this one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setLastOutletWarningOpen(false)}>
              Got it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
